<script setup>
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import MyAiChat from 'dashboard/components-next/my_ai_chat/MyAiChat.vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import waselConstants from 'dashboard/constants/globals';

defineProps({
  conversationInboxType: {
    type: String,
    default: '',
  },
});

const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();
const { isEnterprise } = useConfig();
const { width: windowWidth } = useWindowSize();

const currentUser = useMapGetter('getCurrentUser');
const assistants = useMapGetter('myAiAssistants/getRecords');
const uiFlags = useMapGetter('myAiAssistants/getUIFlags');
const inboxAssistant = useMapGetter('getMyAiChatAssistant');
const currentChat = useMapGetter('getSelectedChat');

const isSmallScreen = computed(
  () => windowWidth.value < waselConstants.SMALL_SCREEN_BREAKPOINT
);

const selectedMyAiChatThreadId = ref(null);
const messages = computed(() =>
  store.getters['myAiChatMessages/getMessagesByThreadId'](
    selectedMyAiChatThreadId.value
  )
);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const selectedAssistantId = ref(null);

const activeAssistant = computed(() => {
  const preferredId = uiSettings.value.preferred_my_ai_assistant_id;

  // If the user has selected a specific assistant, it takes first preference for MyAiChat.
  if (preferredId) {
    const preferredAssistant = assistants.value.find(a => a.id === preferredId);
    // Return the preferred assistant if found, otherwise continue to next cases
    if (preferredAssistant) return preferredAssistant;
  }

  // If the above is not available, the assistant connected to the inbox takes preference.
  if (inboxAssistant.value) {
    const inboxMatchedAssistant = assistants.value.find(
      a => a.id === inboxAssistant.value.id
    );
    if (inboxMatchedAssistant) return inboxMatchedAssistant;
  }
  // If neither of the above is available, the first assistant in the account takes preference.
  return assistants.value[0];
});

const closeMyAiChatPanel = () => {
  if (isSmallScreen.value && uiSettings.value?.is_my_ai_chat_panel_open) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_my_ai_chat_panel_open: false,
    });
  }
};

const setAssistant = async assistant => {
  selectedAssistantId.value = assistant.id;
  await updateUISettings({
    preferred_my_ai_assistant_id: assistant.id,
  });
};

const shouldShowMyAiChatPanel = computed(() => {
  if (!isEnterprise) {
    return false;
  }
  const isMyAiEnabled = isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.MY_AI
  );
  const { is_my_ai_chat_panel_open: isMyAiChatPanelOpen } = uiSettings.value;
  return isMyAiEnabled && isMyAiChatPanelOpen && !uiFlags.value.fetchingList;
});

const handleReset = () => {
  selectedMyAiChatThreadId.value = null;
};

const sendMessage = async message => {
  try {
    if (selectedMyAiChatThreadId.value) {
      await store.dispatch('myAiChatMessages/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        threadId: selectedMyAiChatThreadId.value,
        message,
      });
    } else {
      const response = await store.dispatch('myAiChatThreads/create', {
        assistant_id: activeAssistant.value.id,
        conversation_id: currentChat.value?.id,
        message,
      });
      selectedMyAiChatThreadId.value = response.id;
    }
  } catch (error) {
    useAlert(error.message);
  }
};

onMounted(() => {
  if (isEnterprise) {
    store.dispatch('myAiAssistants/get');
  }
});
</script>

<template>
  <div
    v-if="shouldShowMyAiChatPanel"
    v-on-click-outside="() => closeMyAiChatPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex-col fixed top-0 ltr:right-0 rtl:left-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': shouldShowMyAiChatPanel,
        'md:hidden': !shouldShowMyAiChatPanel,
      },
    ]"
  >
    <MyAiChat
      :messages="messages"
      :support-agent="currentUser"
      :conversation-inbox-type="conversationInboxType"
      :assistants="assistants"
      :active-assistant="activeAssistant"
      @set-assistant="setAssistant"
      @send-message="sendMessage"
      @reset="handleReset"
    />
  </div>
  <template v-else />
</template>
